/**
 * deliverable-fill-route.js — client-saas splash bounce for a deliverable
 * review link (PUBLIC). Advertising Plan is the first kind; this page is
 * kind-agnostic — it renders whatever template the deliverable was authored
 * against.
 *
 * Landing page for the link a reseller sends a client:
 *   #/splash?dl=<deliverable guid>&token=<token>
 *
 * No account, no sign-in — funneled through splash exactly like invite and
 * questionnaire links (see core/deliverableLink.js). Fetches the template
 * sections + the reseller-AUTHORED content + any saved review, and renders the
 * sections as the same STEPPER the questionnaire fill page uses. The delta:
 * each section shows the authored plan content READ-ONLY (only fields the
 * account manager actually filled — untouched campaign/budget blocks collapse
 * away) followed by the client's own review fields (Approved checkbox,
 * Requested Changes, sign-off) as inputs. collectAnswers() therefore picks up
 * ONLY the review fields — the read-only content blocks deliberately do not
 * carry the data-question-id contract.
 *
 * THREE STATES, off ONE link, decided by the SERVER's verdict (status +
 * editability), never re-derived here:
 *   fill   — status SENT. Submits with POST /public/:guid/submit.
 *   edit   — status SUBMITTED and editability.editable. Same stepper,
 *            pre-filled, saving with PUT /public/:guid.
 *   locked — SUBMITTED but no longer editable (finalized, or past the 14-day
 *            window). Read-only, plus why it is closed.
 */

import AuthController from '../services/AuthController.js';
import { getDeliverableLinkParams } from '../core/deliverableLink.js';
import { mfmLockupColorUrl } from '../core/coBrand.js';
// The shared answers contract: answerDisplay for the read-only plan values,
// collectAnswers as the counterpart of the review-field markup below (same
// data-question-id/data-question-type contract as every other surface).
import {
  answerDisplay, collectAnswers, fmtDateTime, ensureQuestionnaireAnswerStyles,
} from '../../../../shared/questionnaireAnswers.js';
// Repeat-group expansion: a v3 template defines campaign 1 as a prototype and
// the author may have added any number more — expand to every populated
// instance before rendering. v1/v2 templates pass through unchanged.
import { expandSections } from '../../../../shared/deliverableRepeats.js';
// Vite resolves this to the hashed asset URL (base-aware) — the same sketch
// the auth panel and the questionnaire fill page use.
import heroSketch from '../../../../shared/styles/img/auth-hero-sketch.jpg';
import { guardedFetch } from '../core/navGeneration.js';

let stylesInjected = false;

function ensureStyles() {
  if (stylesInjected) return;
  const style = document.createElement('style');
  style.id = 'm5t-deliverable-fill-styles';
  style.textContent = `
    .dlf-page {
      /* FORCED-LIGHT SURFACE: this is a client-facing document, not an app
         screen. The app's theme tokens can be dark-remapped, which made every
         var()-colored heading render pale gray on the white card whenever the
         viewer's OS was in dark mode (reported twice before the cause was
         found). Pin the light palette locally so no theme touches it. */
      --color-text: #0B1220; --color-text-muted: #4A5A78; --color-text-subtle: #6C7C9A;
      --color-border: #D4DCE8; --color-bg: #fff; --color-surface: #F4F7FB; --color-surface-2: #EAF0F7;
      --color-primary-500: #0088DD; --color-primary-600: #0072BA; --color-primary-700: #00558D; --color-primary-800: #003961;
      --color-danger: #DD4124; --color-success: #009473; --color-success-soft: #E6F5F1;
      --color-accent-soft: rgba(0,114,186,0.15);
      min-height: 100%;   /* the scroll pane, not the viewport */ padding: 32px 20px 64px; position: relative;
      /* Same brand-blue field as the questionnaire fill page (auth panel one
         step lighter), so every emailed-link surface reads as one family. */
      background:
        radial-gradient(120% 120% at 80% 10%, rgba(34, 160, 255, 0.30) 0%, transparent 55%),
        linear-gradient(160deg, var(--color-primary-500, #0088DD) 0%, var(--color-primary-700, #00558D) 70%, var(--color-primary-800, #003961) 100%);
      color: var(--color-text, #0B1220);
      color-scheme: light; font-family: var(--font-body, 'Inter', system-ui, sans-serif);
    }
    .dlf-page::before {
      content: ""; position: fixed; inset: 0; z-index: 0;
      background: url("${heroSketch}") center / cover no-repeat;
      mix-blend-mode: multiply;
      opacity: 0.24; pointer-events: none;
    }
    .dlf-card {
      width: min(720px, 100%); margin: 0 auto; background: var(--color-bg, #fff);
      border: 1px solid var(--color-border, #D4DCE8); border-radius: var(--radius-lg, 16px);
      box-shadow: var(--shadow-md, 0 8px 24px rgba(11,18,32,0.10)); padding: 36px 32px;
      position: relative; z-index: 1;
    }
    .dlf-card h1 { font-family: var(--font-display, inherit); font-size: 1.5rem; font-weight: 600; margin: 0 0 6px; color: var(--color-primary-600, #0072BA); }
    .dlf-card .dlf-sub { font-size: 0.9rem; color: var(--color-text-muted, #4A5A78); margin: 0 0 28px; }
    .dlf-brand { height: 38px; width: auto; display: block; margin: 0 0 18px; }
    .dlf-section { margin-bottom: 8px; }
    .dlf-section[hidden] { display: none; }
    /* Branded blue, per owner ruling: section titles are never gray. */
    .dlf-section h2 { font-size: 1.05rem; font-weight: 600; margin: 0 0 10px; padding-bottom: 8px; border-bottom: 1px solid var(--color-border, #D4DCE8); color: var(--color-primary-700, #00558D); }
    .dlf-intro { font-size: 0.85rem; color: var(--color-text-muted, #4A5A78); margin: 0 0 16px; }
    /* The authored plan content — the document the client is approving. A
       tinted panel keeps "their plan" visually distinct from "your review". */
    .dlf-plan { background: var(--color-surface, #F4F7FB); border: 1px solid var(--color-border, #D4DCE8); border-radius: var(--radius-md, 10px); padding: 14px 16px; margin: 0 0 18px; }
    .dlf-plan__item { margin-bottom: 12px; }
    .dlf-plan__item:last-child { margin-bottom: 0; }
    /* Full ink, not gray: these are the headlines over every plan value and
       gray-on-white failed readability twice (owner). */
    .dlf-plan__label { font-size: 0.78rem; font-weight: 700; color: var(--color-text, #0B1220); margin-bottom: 2px; }
    .dlf-plan__value { font-size: 0.92rem; white-space: pre-wrap; }
    .dlf-plan__empty { font-size: 0.85rem; color: var(--color-text-subtle, #6C7C9A); font-style: italic; }
    .dlf-help { font-size: 0.8rem; color: var(--color-text-subtle, #6C7C9A); margin: -2px 0 6px; }
    .dlf-field { margin-bottom: 18px; }
    .dlf-field label { display: block; font-size: 0.85rem; font-weight: 600; margin-bottom: 6px; }
    .dlf-field input[type="text"], .dlf-field textarea {
      width: 100%; box-sizing: border-box; font: inherit; font-size: 0.92rem; padding: 10px 12px;
      border: 1px solid var(--color-border, #D4DCE8); border-radius: var(--radius-md, 10px);
      background: var(--color-bg, #fff); color: var(--color-text, #0B1220);
    }
    .dlf-field textarea { min-height: 76px; resize: vertical; }
    .dlf-field input:focus, .dlf-field textarea:focus { outline: none; border-color: var(--color-primary-600, #0072BA); box-shadow: 0 0 0 3px var(--color-accent-soft, rgba(0,114,186,0.15)); }
    .dlf-checkbox-group { display: flex; flex-wrap: wrap; gap: 10px 20px; }
    .dlf-checkbox-row { display: flex; align-items: center; gap: 6px; font-size: 0.9rem; }
    /* Gated review fields: visible but inert until the section's decision
       ("I have edits") unlocks them, or while edits gray out the sign-off. */
    .dlf-field.dlf-gated { opacity: 0.45; }
    .dlf-field.dlf-gated input, .dlf-field.dlf-gated textarea { background: var(--color-surface, #F4F7FB); cursor: not-allowed; }
    .dlf-btn {
      width: 100%; appearance: none; border: 0; cursor: pointer; font: inherit; font-size: 0.98rem; font-weight: 600;
      padding: 13px; border-radius: var(--radius-md, 10px); background: var(--color-primary-600, #0072BA); color: #fff; margin-top: 8px;
    }
    .dlf-btn:hover { background: var(--color-primary-700, #00558D); }
    .dlf-btn[disabled] { opacity: 0.6; cursor: default; }
    .dlf-btn--ghost {
      appearance: none; cursor: pointer; font: inherit; font-size: 0.98rem; font-weight: 600;
      padding: 13px 18px; border-radius: var(--radius-md, 10px);
      border: 1px solid var(--color-border, #D4DCE8); background: var(--color-bg, #fff); color: var(--color-text, #0B1220);
      display: inline-flex; align-items: center; gap: 4px; white-space: nowrap;
    }
    .dlf-btn--ghost:hover { background: var(--color-surface, #F4F7FB); }
    .dlf-btn--ghost .material-symbols-outlined, .dlf-btn .material-symbols-outlined { font-size: 1.1rem; }
    .dlf-msg { font-size: 0.85rem; margin: 14px 0 0; min-height: 1em; }
    .dlf-msg.error { color: var(--color-danger, #DD4124); }
    .dlf-msg.ok { color: var(--color-success, #009473); }
    .dlf-state { text-align: center; padding: 20px 0; }
    .dlf-state .material-symbols-outlined { font-size: 2.4rem; color: var(--color-primary-600, #0072BA); }
    /* ── stepper chrome — same roller as the questionnaire fill page ── */
    .dlf-progress { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin: 0 0 18px; }
    .dlf-progress-label { font-size: 0.78rem; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: var(--color-primary-700, #00558D); }
    /* Locked/read-only labels inherit the shared gray — override to ink here. */
    .dlf-page .qm-qa__q { color: var(--color-text, #0B1220); }
    .dlf-dots { display: flex; gap: 8px; }
    .dlf-dot {
      appearance: none; border: 0; cursor: pointer; padding: 0;
      width: 10px; height: 10px; border-radius: 50%;
      background: var(--color-border, #D4DCE8); transition: background .15s ease, transform .15s ease;
    }
    .dlf-dot[aria-current="step"] { background: var(--color-primary-600, #0072BA); transform: scale(1.25); }
    .dlf-dot:focus-visible { outline: none; box-shadow: 0 0 0 3px var(--color-accent-soft, rgba(0,114,186,0.25)); }
    .dlf-section.dlf-enter { animation: dlfSlideIn .18s ease; }
    @keyframes dlfSlideIn { from { opacity: 0; transform: translateX(var(--dlf-dx, 14px)); } }
    html.ff-no-animations .dlf-section.dlf-enter { animation: none; }
    @media (prefers-reduced-motion: reduce) { .dlf-section.dlf-enter { animation: none; } }
    .dlf-nav { display: flex; align-items: center; gap: 10px; margin-top: 20px; }
    .dlf-nav .dlf-btn { margin-top: 0; }
    /* ── return-visit chrome (edit + locked states) ── */
    .dlf-note {
      display: flex; align-items: flex-start; gap: 8px; padding: 11px 13px; margin: 0 0 20px;
      border-radius: var(--radius-md, 10px); background: var(--color-surface, #F4F7FB);
      font-size: 0.85rem; line-height: 1.45; color: var(--color-text, #0B1220);
    }
    /* display:flex above beats the UA's [hidden] rule — restate it, or the
       edits explainer shows with no edits selected (shipped exactly that). */
    .dlf-note[hidden] { display: none; }
    .dlf-note .material-symbols-outlined { font-size: 1.15rem; flex: 0 0 auto; color: var(--color-primary-600, #0072BA); }
    .dlf-note--locked .material-symbols-outlined { color: var(--color-text-muted, #4A5A78); }
    .dlf-note--done { background: var(--color-success-soft, #E6F5F1); }
    .dlf-note--done .material-symbols-outlined { color: var(--color-success, #009473); }
  `;
  document.head.appendChild(style);
  // The .qm-* answer styles, for the locked read-only review fields — shared
  // with every other answers surface so this page cannot drift.
  ensureQuestionnaireAnswerStyles();
  stylesInjected = true;
}

function apiBase() {
  return AuthController.getConfig?.().BASE_URL || '';
}

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

const isAm = (q) => q.audience === 'am';

/** Does this authored field carry a value? (string, or non-empty array for checkbox_group) */
function hasContent(q, content) {
  const v = content?.[q.id];
  return Array.isArray(v) ? v.length > 0 : !!(v && String(v).trim());
}

/**
 * The authored plan content for one section, read-only. Only fields the
 * account manager actually filled render — an untouched Campaign 3/4 block
 * (or any blank field) collapses away rather than showing rows of blanks.
 * Deliberately WITHOUT data-question-id: collectAnswers must never read these.
 */
function planHtml(section, content) {
  const items = (section.questions || []).filter((q) => isAm(q) && hasContent(q, content));
  if (!items.length) return '';
  return `<div class="dlf-plan">${items.map((q) => `
    <div class="dlf-plan__item">
      <div class="dlf-plan__label">${escapeHtml(q.label)}</div>
      <div class="dlf-plan__value">${answerDisplay(q, content)}</div>
    </div>`).join('')}</div>`;
}

/** One review field as an input — same data-question-id/type contract as the questionnaire's fieldHtml. */
function fieldHtml(q) {
  const id = `dlf-${q.id}`;
  if (q.type === 'checkbox_group') {
    const options = Array.isArray(q.options) ? q.options : [];
    return `
      <div class="dlf-field" data-question-id="${q.id}" data-question-type="checkbox_group">
        <label>${escapeHtml(q.label)}</label>
        ${q.help ? `<p class="dlf-help">${escapeHtml(q.help)}</p>` : ''}
        <div class="dlf-checkbox-group">
          ${options.map((opt, i) => `
            <label class="dlf-checkbox-row">
              <input type="checkbox" id="${id}-${i}" value="${escapeHtml(opt)}" />
              ${escapeHtml(opt)}
            </label>`).join('')}
        </div>
      </div>`;
  }
  if (q.type === 'textarea') {
    return `
      <div class="dlf-field" data-question-id="${q.id}" data-question-type="textarea">
        <label for="${id}">${escapeHtml(q.label)}</label>
        ${q.help ? `<p class="dlf-help">${escapeHtml(q.help)}</p>` : ''}
        <textarea id="${id}"></textarea>
      </div>`;
  }
  return `
    <div class="dlf-field" data-question-id="${q.id}" data-question-type="text">
      <label for="${id}">${escapeHtml(q.label)}</label>
      ${q.help ? `<p class="dlf-help">${escapeHtml(q.help)}</p>` : ''}
      <input type="text" id="${id}" />
    </div>`;
}

/** One review field, read-only (locked state). */
function reviewReadOnlyHtml(q, answers) {
  return `
    <div class="qm-qa">
      <div class="qm-qa__q">${escapeHtml(q.label)}</div>
      <div class="qm-qa__a">${answerDisplay(q, answers) || '<span class="qm-qa__a--empty">Left blank</span>'}</div>
    </div>`;
}

/**
 * One section's full body: intro, authored plan panel, then the review
 * fields. No eyebrow above the review — the decision field's own label plus
 * its two options are the entire action, and a stacked "Your review /
 * Your Review / Approved" read as three headlines for one checkbox (owner).
 */
function sectionBodyHtml(section, content, { readOnly = false, answers = {} } = {}) {
  const clQuestions = (section.questions || []).filter((q) => !isAm(q));
  const plan = planHtml(section, content);
  const review = clQuestions.map((q) => (readOnly ? reviewReadOnlyHtml(q, answers) : fieldHtml(q))).join('');
  return `
    ${section.intro ? `<p class="dlf-intro">${escapeHtml(section.intro)}</p>` : ''}
    ${plan}
    ${review}`;
}

function stateHtml(icon, title, body) {
  return `
    <div class="dlf-page"><div class="dlf-card"><div class="dlf-state">
      <span class="material-symbols-outlined">${icon}</span>
      <h1>${escapeHtml(title)}</h1>
      <p class="dlf-sub">${escapeHtml(body)}</p>
    </div></div></div>`;
}

export function render() {
  ensureStyles();
  // Real content is fetched async in init() — this is just the mount point,
  // matching how the rest of this app renders.
  return `<div id="dlf-root">${stateHtml('hourglass_top', 'Loading your plan…', 'One moment.')}</div>`;
}

export async function init() {
  const root = document.getElementById('dlf-root');
  if (!root) return;

  const params = getDeliverableLinkParams();
  if (!params) {
    root.innerHTML = stateHtml('error', 'Invalid link', 'This link is missing required information. Please use the link exactly as sent.');
    return;
  }

  let data;
  try {
    const res = await guardedFetch(`${apiBase()}/m5t/v5/m5mDeliverable/public/${encodeURIComponent(params.guid)}?token=${encodeURIComponent(params.token)}`);
    const body = await res.json().catch(() => ({}));
    if (!res.ok || !body.ok) {
      const msg = res.status === 404
        ? (body.error || 'This link is invalid or has expired.')
        : 'Something went wrong loading your plan. Please try again shortly.';
      root.innerHTML = stateHtml('error', 'Cannot open this plan', msg);
      return;
    }
    data = body.data;
  } catch (err) {
    console.error('[deliverable-fill] load error:', err);
    root.innerHTML = stateHtml('wifi_off', 'Connection problem', 'Could not reach the server. Please check your connection and try again.');
    return;
  }

  const mode = modeFor(data);
  if (mode === 'locked') {
    renderLocked(root, data);
    return;
  }
  renderForm(root, data, params, mode);
}

/**
 * Which of the three states this deliverable is in — straight from the
 * server's status + editability verdict, never re-derived here. Same
 * older-API fallback as the questionnaire page: an unlocked SUBMITTED row
 * shows the form; the PUT still enforces the truth.
 */
function modeFor(data) {
  // There is NO preview mode: the link is live from the moment the
  // deliverable exists (owner ruling — a link shared directly, outside the
  // email flow, must accept the review like any other). DRAFT and SENT both
  // render the working form.
  if (data.status !== 'SUBMITTED') return 'fill';
  const edit = data.editability || { editable: !data.locked_tz };
  return edit.editable ? 'edit' : 'locked';
}

/** Why editing is closed, in the same vocabulary the staff surfaces use. */
function lockedReasonText(data) {
  const edit = data.editability || {};
  if (edit.reason === 'finalized') {
    return 'Your account manager has finalised this plan, so the review can no longer be changed here.';
  }
  const when = edit.deadline ? ` The window closed on ${fmtDateTime(edit.deadline)}.` : '';
  return `A review can be changed for 14 days after it is submitted.${when}`;
}

/** The read-only state: the authored plan plus the client's review, per section. */
function renderLocked(root, data) {
  const sections = expandSections(data.template?.sections || [], data.content, data.answers);
  root.innerHTML = `
    <div class="dlf-page"><div class="dlf-card">
      <img class="dlf-brand" src="${mfmLockupColorUrl}" alt="Mach Five Marketing" height="34" />
      <h1>${escapeHtml(data.template?.name || 'Your Plan')}</h1>
      <p class="dlf-sub">${data.submitted_tz ? `Submitted ${escapeHtml(fmtDateTime(data.submitted_tz))}.` : 'Submitted.'} Here is the plan and your review.</p>
      <div class="dlf-note dlf-note--locked">
        <span class="material-symbols-outlined" aria-hidden="true">lock</span>
        <span>${escapeHtml(lockedReasonText(data))} To change anything now, contact your account manager.</span>
      </div>
      ${sections.map((s) => `
        <div class="dlf-section">
          <h2>${escapeHtml(s.title)}</h2>
          ${sectionBodyHtml(s, data.content || {}, { readOnly: true, answers: data.answers || {} })}
        </div>`).join('')}
    </div></div>`;
}

/**
 * The stepper — used for BOTH the first review and the client's later edit,
 * differing only in copy and verb (POST submit vs PUT). Same roller mechanics
 * as the questionnaire fill page: every section stays mounted in the one
 * <form> (hidden, not unmounted), so review answers survive navigation and
 * one collection reads them all.
 */
function renderForm(root, data, params, mode) {
  const editing = mode === 'edit';
  const sections = expandSections(data.template?.sections || [], data.content, data.answers);
  const savedAnswers = data.answers || {};
  const content = data.content || {};
  const deadline = data.editability?.deadline;

  const intro = editing
    ? 'Change anything you need to, then save. Everything you sent before is already filled in.'
    : 'Review each section and mark it Approved, or note what you want changed.';
  const noteHtml = editing
    ? `<div class="dlf-note">
         <span class="material-symbols-outlined" aria-hidden="true">edit_note</span>
         <span>You already submitted this${data.submitted_tz ? ` on ${escapeHtml(fmtDateTime(data.submitted_tz))}` : ''}.${
           deadline ? ` You can make changes until <strong>${escapeHtml(fmtDateTime(deadline))}</strong>.` : ''
         }</span>
       </div>`
    : '';

  root.innerHTML = `
    <div class="dlf-page"><div class="dlf-card">
      <img class="dlf-brand" src="${mfmLockupColorUrl}" alt="Mach Five Marketing" height="34" />
      <h1>${escapeHtml(data.template?.name || 'Your Plan')}</h1>
      <p class="dlf-sub">${escapeHtml(intro)}</p>
      ${noteHtml}
      <div class="dlf-progress">
        <span class="dlf-progress-label" id="dlf-progress-label"></span>
        <div class="dlf-dots" role="group" aria-label="Sections">
          ${sections.map((s, i) => `<button type="button" class="dlf-dot" data-step="${i}" aria-label="${escapeHtml(s.title)}"></button>`).join('')}
        </div>
      </div>
      <form id="dlf-form" novalidate>
        ${sections.map((s, i) => `
          <div class="dlf-section" data-step="${i}" hidden>
            <h2>${escapeHtml(s.title)}</h2>
            ${sectionBodyHtml(s, content)}
          </div>`).join('')}
        <div class="dlf-nav">
          <button class="dlf-btn--ghost" type="button" id="dlf-back"><span class="material-symbols-outlined" aria-hidden="true">arrow_back</span>Back</button>
          <button class="dlf-btn" type="button" id="dlf-next">Next<span class="material-symbols-outlined" aria-hidden="true">arrow_forward</span></button>
          <button class="dlf-btn" type="submit" id="dlf-submit">${editing ? 'Save changes' : 'Submit review'}</button>
        </div>
        <p class="dlf-msg" id="dlf-msg" role="status"></p>
      </form>
    </div></div>`;

  // ── stepper: one section visible at a time; every section stays mounted. ──
  const stepEls = [...root.querySelectorAll('.dlf-section[data-step]')];
  const dotEls = [...root.querySelectorAll('.dlf-dot')];
  const backBtn = document.getElementById('dlf-back');
  const nextBtn = document.getElementById('dlf-next');
  const submitBtn = document.getElementById('dlf-submit');
  const progressLabel = document.getElementById('dlf-progress-label');
  let step = 0;

  function showStep(n, fromDirection = 1) {
    step = Math.max(0, Math.min(sections.length - 1, n));
    stepEls.forEach((el, i) => {
      el.hidden = i !== step;
      el.classList.remove('dlf-enter');
    });
    const active = stepEls[step];
    if (active) {
      active.style.setProperty('--dlf-dx', `${fromDirection >= 0 ? 14 : -14}px`);
      // Re-trigger the entry animation (removed above; reflow restarts it).
      void active.offsetWidth;
      active.classList.add('dlf-enter');
    }
    dotEls.forEach((d, i) => {
      if (i === step) d.setAttribute('aria-current', 'step');
      else d.removeAttribute('aria-current');
    });
    if (progressLabel) progressLabel.textContent = `Section ${step + 1} of ${sections.length}`;
    backBtn.style.visibility = step === 0 ? 'hidden' : '';
    nextBtn.style.display = step === sections.length - 1 ? 'none' : '';
    if (submitBtn) submitBtn.style.display = step === sections.length - 1 ? '' : 'none';
    root.querySelector('.dlf-card')?.scrollIntoView({ block: 'start' });
  }

  backBtn.addEventListener('click', () => showStep(step - 1, -1));
  nextBtn.addEventListener('click', () => showStep(step + 1, 1));
  dotEls.forEach((d) => d.addEventListener('click', () => {
    const target = Number(d.dataset.step) || 0;
    showStep(target, target >= step ? 1 : -1);
  }));
  showStep(0);

  // Pre-fill any saved review — in edit mode, the full set already submitted.
  root.querySelectorAll('[data-question-id]').forEach((fieldEl) => {
    const qid = fieldEl.dataset.questionId;
    const type = fieldEl.dataset.questionType;
    const saved = savedAnswers[qid];
    if (saved == null) return;
    if (type === 'checkbox_group') {
      const selected = Array.isArray(saved) ? saved : [];
      fieldEl.querySelectorAll('input[type="checkbox"]').forEach((cb) => { cb.checked = selected.includes(cb.value); });
    } else {
      const input = fieldEl.querySelector('input,textarea');
      if (input) input.value = saved;
    }
  });

  // ── decision gating (template v5+): each section's review is an explicit
  // choice — Approved OR "I have edits". Feedback fields stay inert until
  // "I have edits" unlocks them; while ANY section has edits, the sign-off
  // approval grays out and the submit becomes "Send back with edits" (owner
  // ruling: a client with feedback was staring at an approve-only checkbox
  // with no obvious path to give it). Decision groups are recognized by
  // carrying BOTH options, so pinned v1-v4 rows (Approved only) keep their
  // ungated behavior. ──
  const DECISION_EDITS = 'I have edits';
  const decisions = [...root.querySelectorAll('.dlf-field[data-question-type="checkbox_group"]')].filter((el) => {
    const opts = [...el.querySelectorAll('input[type="checkbox"]')].map((c) => c.value);
    return opts.includes('Approved') && opts.includes(DECISION_EDITS);
  });
  const signApprove = root.querySelector('.dlf-field[data-question-id="cl_sign_approve"]');
  const applyGating = () => {
    let anyEdits = false;
    decisions.forEach((dec) => {
      const section = dec.closest('.dlf-section');
      const checked = [...dec.querySelectorAll('input:checked')].map((c) => c.value);
      const editsOn = checked.includes(DECISION_EDITS);
      if (editsOn) anyEdits = true;
      section?.querySelectorAll('.dlf-field').forEach((f) => {
        if (f === dec) return;
        f.classList.toggle('dlf-gated', !editsOn);
        f.querySelectorAll('input,textarea').forEach((i) => { i.disabled = !editsOn; });
      });
    });
    if (signApprove && decisions.length) {
      signApprove.classList.toggle('dlf-gated', anyEdits);
      signApprove.querySelectorAll('input').forEach((i) => {
        i.disabled = anyEdits;
        if (anyEdits) i.checked = false;
      });
      // The sign-off must explain ITSELF in edits mode: a grayed approval with
      // no words reads as a dead end, when the actual action is the send
      // button right below it.
      const section = signApprove.closest('.dlf-section');
      let note = section?.querySelector('#dlf-edits-note');
      if (!note && section) {
        note = document.createElement('div');
        note.id = 'dlf-edits-note';
        note.className = 'dlf-note';
        note.innerHTML = '<span class="material-symbols-outlined" aria-hidden="true">forward_to_inbox</span>'
          + '<span>You marked sections with edits, so approval is off for this round. Use <strong>Send back with edits</strong> below and your account manager will follow up with a revised plan.</span>';
        section.insertBefore(note, section.querySelector('.dlf-field'));
      }
      if (note) note.hidden = !anyEdits;
      const sb = document.getElementById('dlf-submit');
      if (sb) sb.textContent = editing ? 'Save changes' : (anyEdits ? 'Send back with edits' : 'Submit review');
    }
  };
  decisions.forEach((dec) => {
    dec.querySelectorAll('input[type="checkbox"]').forEach((cb) => {
      cb.addEventListener('change', () => {
        // Approved and "I have edits" are one decision, not two checkboxes.
        if (cb.checked) dec.querySelectorAll('input[type="checkbox"]').forEach((o) => { if (o !== cb) o.checked = false; });
        applyGating();
      });
    });
  });
  if (decisions.length) applyGating();

  const form = document.getElementById('dlf-form');
  const btn = document.getElementById('dlf-submit');
  const msg = document.getElementById('dlf-msg');
  const setMsg = (text, kind = 'error') => { msg.textContent = text; msg.className = `dlf-msg ${kind}`; };

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    // A review is its decisions: block an accidental empty submit by walking
    // the client to the first undecided section instead of recording nothing
    // (the server enforces the same rule). Only templates with the two-option
    // decisions carry the requirement.
    if (decisions.length) {
      const missing = decisions.find((dec) => !dec.querySelector('input:checked'));
      if (missing) {
        showStep(Number(missing.closest('.dlf-section')?.dataset.step) || 0);
        setMsg('Mark each section Approved or I have edits.');
        return;
      }
      const anyEdits = decisions.some((dec) => [...dec.querySelectorAll('input:checked')].some((c) => c.value === DECISION_EDITS));
      if (!anyEdits && signApprove && !signApprove.querySelector('input:checked')) {
        showStep(sections.length - 1);
        setMsg('To approve the plan, check I approve this plan.');
        return;
      }
    }
    // The shared collector reads only the [data-question-id] wrappers — the
    // review fields. The read-only plan panels never carry the contract.
    const answers = collectAnswers(root);
    const base = `${apiBase()}/m5t/v5/m5mDeliverable/public/${encodeURIComponent(params.guid)}`;
    const url = editing ? `${base}?token=${encodeURIComponent(params.token)}`
      : `${base}/submit?token=${encodeURIComponent(params.token)}`;

    btn.disabled = true;
    setMsg(editing ? 'Saving…' : 'Submitting…', 'ok');

    let res;
    try {
      res = await guardedFetch(url, {
        method: editing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        // content_fingerprint binds this approval to the plan version rendered
        // above — the server refuses (409 content_changed) if the plan moved
        // after this tab loaded, and we reload the current version instead.
        body: JSON.stringify(editing ? { answers } : { answers, content_fingerprint: data.content_fingerprint || '' }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.ok) {
        // 423: the window closed (or an admin finalized) while this form was
        // open. Reload into the read-only state rather than stranding the
        // client in a form that can never save.
        if (res.status === 423) {
          setMsg(body.error || 'This review is now locked.', 'error');
          await init();
          return;
        }
        // The plan was edited after this tab loaded. The refusal protects the
        // client (their sign-off must be of what they SAW) — reload so they
        // review the current version, and re-plant the explanation AFTER the
        // reload (init() rebuilds the page, which would otherwise eat it).
        if (res.status === 409 && body.reason === 'content_changed') {
          await init();
          const freshMsg = document.getElementById('dlf-msg');
          if (freshMsg) {
            freshMsg.textContent = body.error || 'This plan was updated after you opened it. Review the latest version and submit again.';
            freshMsg.className = 'dlf-msg error';
          }
          return;
        }
        const m = res.status === 409
          ? (editing ? 'This plan is not open for changes.' : 'This plan has already been submitted.')
          : res.status === 404
            ? 'This link is invalid or has expired.'
            : (body.error || `Could not ${editing ? 'save' : 'submit'} (HTTP ${res.status}).`);
        setMsg(m);
        btn.disabled = false;
        return;
      }
      renderDone(root, editing, body.data?.editability?.deadline);
    } catch (err) {
      console.error(`[deliverable-fill] ${editing ? 'edit' : 'submit'} error:`, err);
      setMsg('Something went wrong. Please try again.');
      btn.disabled = false;
    }
  });
}

/**
 * The confirmation state, after a first submission or a later change. States
 * that the link keeps working and until when — the page's half of the same
 * promise the confirmation email makes. "Make another change" re-runs init()
 * so what comes back is the server's current verdict.
 */
function renderDone(root, wasEdit, deadline) {
  const until = deadline ? ` until ${fmtDateTime(deadline)}` : '';
  root.innerHTML = `
    <div class="dlf-page"><div class="dlf-card"><div class="dlf-state">
      <span class="material-symbols-outlined">check_circle</span>
      <h1>${wasEdit ? 'Changes saved' : 'Thank you'}</h1>
      <p class="dlf-sub">${wasEdit
        ? 'Your account manager will see the updated review.'
        : 'Your review has been submitted. Your account manager will be in touch.'}</p>
      <div class="dlf-note dlf-note--done">
        <span class="material-symbols-outlined" aria-hidden="true">schedule</span>
        <span>Need to change something? This same link keeps working${escapeHtml(until)}. Keep the email we sent you and open it again any time before then.</span>
      </div>
      <button class="dlf-btn" type="button" id="dlf-again">Make another change</button>
    </div></div></div>`;
  document.getElementById('dlf-again')?.addEventListener('click', () => { init(); });
}

export default { render, init };
